import React, { useEffect, useRef, useState } from 'react';
import { fmtCost, fmtClock, fmtTimeOfDay, fmtDate, ClientSlideOut } from './vendor';
import EntryMenu from './EntryMenu';
import EntryProject from './EntryProject';
import { normalizeLabel, MAX_LABEL_LENGTH } from './projectLabel';
import EntryMoveDialog, { NONEXISTENT_TIME_MESSAGE } from './EntryMoveDialog';
import {
  parseDuration, minutesFromEndTime, toTimeInputValue, toDateInputValue, endOf, floorToMinute,
  resolveStartEdit, moveToDate, MAX_DURATION_MINUTES,
} from './entryTime';

/**
 * One timesheet entry, Toggl-style: a flat row (not a card) that sits inside a day
 * group. Human and agent entries share the row and are never separated.
 *
 * Entry shape (fn_my_timesheet):
 *   { id, at, minutes, what, client, client_id, task, task_id, service,
 *     actor_type:'human'|'agent', cost, needs_review }
 *
 * `at` + `minutes` derive the clock RANGE (start = at, end = at + minutes); the
 * backend stores no explicit end. An agent entry has no time to run, so it shows its
 * COST where a human shows a duration, and no start/stop affordances.
 *
 * A human row on the viewer's own timesheet (`editable`, with `onCorrect`) edits in
 * place: description, end time and duration are inputs, committed on blur (Enter blurs,
 * Esc reverts). Any other row renders plain text with the Edit button that opens the
 * correction dialog.
 *
 * A field is written only if the user changed it: each input tracks a dirty flag set by
 * `onChange`, and a blur on an untouched field does nothing. Several displays are lossy
 * (an end time drops seconds, a duration rounds, a text input flattens newlines), so a
 * value is never judged "changed" by re-parsing what the input shows. Three entries
 * cannot be edited in a field at all and keep Edit: a description with a line break is
 * shown read-only, and an entry of a day or more shows its end as text.
 *
 * The description input shows the capitalised text, by the row's display convention, so
 * editing a description stores its first letter capitalised.
 *
 * @param {object} entry
 * @param {(entry:object)=>void} [onEdit]   Opens the correction flow.
 * @param {(entry:object)=>void} [onStart]  Starts a fresh timer from this entry.
 * @param {(entry:object)=>Promise<{status:'saved'|'failed', message?:string}>} [onDuplicate]
 *   Logs a copy of the entry. Resolves with a status rather than throwing; a `failed`
 *   result's message is shown under the row.
 * @param {(entry:object, change:{minutes:number, description:string})=>Promise<{status:'saved'|'failed', message?:string}>} [onCorrect]
 *   Saves an inline edit. Same status contract as `onDuplicate`; the row stays locked
 *   until it settles, which is after the timesheet refetch.
 * @param {boolean} [editable] Whether the viewer may change this entry (it is their own
 *   timesheet). Gates the inline inputs and the `⋯` menu; defaults to false so a caller
 *   must opt in.
 * @param {boolean} [locked] Another write on the page is still in flight (it has not yet
 *   been refetched). Disables the inputs, the menu and Edit/Verify, as the row's own
 *   pending state does, without changing what the row shows.
 * @param {{onDelete:(entry:object)=>Promise<{status:'saved'|'failed', message?:string}>, undoable:boolean}} [deletion]
 *   Present only when the delete capability is on. Adds Delete to the `⋯` menu of an
 *   editable human row, with an in-row confirmation: nothing is sent until the user
 *   presses Delete in it. `undoable` says whether an Undo will follow (the confirmation
 *   otherwise warns that it cannot be undone). `onDelete` has the `onCorrect` status
 *   contract: `saved`, or `failed` with the message to show under the row. Without it the
 *   row has no Delete.
 * @param {{key:string, text:string}[]} [hints] Advisory reasons the entry looks invalid
 *   (needs review, overlap, future, no description), shown as chips beside the description.
 *   Never a precondition for anything.
 * @param {{minDate:string, maxDate:string, checking:boolean, loadClients:Function, loadTasks:Function}} [move]
 *   Present only when the move capability is on (see effortCapabilities). It turns on the
 *   editable start time, the date control, the project picker and the menu's Change items,
 *   and carries what they need: the date bounds (the first loaded day and today), whether
 *   an overlap check is running, and the picker's loaders. Without it the row is exactly
 *   what it was before the move controls existed, and `onCorrect` is only ever given
 *   `{minutes, description}`.
 *
 *   A move edit calls `onCorrect(entry, change)` with the new values and only the move
 *   fields that changed: `when` (an ISO start, for a start or date edit), `clientId` and
 *   `taskId`. The handler may refuse the change (an overlap, a time in the future) by
 *   resolving `failed` with the reason, which the row shows and reverts on.
 */
const Icon = ({ path, className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {path}
  </svg>
);

// First letter upper-case, so "review TCM-154…" reads "Review TCM-154…".
const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * The row's wrap layout, in one place. The group of project, times, duration and actions
 * shares the description's line from the `xl` breakpoint (1280px) up and wraps to its own
 * line below it, left-aligned, the project label shrinking (truncating) rather than the group
 * wrapping again. To move the breakpoint, change `xl` to another Tailwind step
 * in the three strings (they are whole class names so the build can see them).
 */
const ROW_WRAP = {
  row: 'flex flex-wrap items-center gap-x-3 gap-y-1 xl:flex-nowrap',
  description: 'w-full xl:w-auto xl:flex-1',
  group: 'flex min-w-0 items-center gap-x-3 xl:contents',
};

/**
 * Whether an element is rendered, not just present: an ancestor up to `root` hidden with
 * `display: none` (the row hides fields below a breakpoint with CSS) makes it unfocusable.
 * Uses `checkVisibility()` where the browser has it, else walks the ancestors.
 *
 * @param {HTMLElement} element
 * @param {HTMLElement|null} root
 * @returns {boolean}
 */
function isRendered(element, root) {
  if (typeof element.checkVisibility === 'function') return element.checkVisibility();
  for (let node = element; node && node !== root?.parentElement; node = node.parentElement) {
    if (window.getComputedStyle(node).display === 'none') return false;
  }
  return true;
}

/**
 * Where focus goes when a row is removed and no toast takes it: the menu of the next human
 * row of the day; else, when the day keeps other rows, its heading; else the week's header.
 * Read from the DOM before the row unmounts.
 *
 * @param {HTMLElement|null} row
 * @returns {HTMLElement|null}
 */
function focusHeir(row) {
  const siblings = Array.from(row?.parentElement?.children || []);
  const next = siblings.slice(siblings.indexOf(row) + 1)
    .map((sibling) => sibling.querySelector('[data-testid="row-menu"]'))
    .find(Boolean);
  if (next) return next;
  if (siblings.length > 1) return row.closest('[data-testid="timesheet-day"]')?.querySelector('h4') ?? null;
  return row?.closest('[data-testid="timesheet-week"]')?.querySelector('[data-testid="week-toggle"]') ?? null;
}

/** Focuses `element`, making a non-interactive one (a heading) focusable first. */
function focusElement(element) {
  if (!element?.isConnected) return;
  if (!element.matches('button, a, input, select, textarea, [tabindex]')) element.setAttribute('tabindex', '-1');
  element.focus();
}

// A borderless input that shows its border on hover and focus, so the row still reads
// as text until the pointer or keyboard reaches a field.
const INPUT_BASE = 'rounded-md border border-transparent bg-transparent px-1.5 py-1 text-sm hover:border-gray-200 dark:hover:border-gray-700 focus:border-[#32B1FF] focus:outline-none focus:ring-1 focus:ring-[#32B1FF] disabled:opacity-60 disabled:hover:border-transparent aria-[invalid=true]:border-red-400 [&::-webkit-calendar-picker-indicator]:hidden';

/** Props for the ARIA wiring of a field that may be the one that just failed. */
const invalidProps = (isInvalid, errorId) => (
  isInvalid ? { 'aria-invalid': 'true', 'aria-describedby': errorId } : {}
);

export default function EffortRow({ entry, onEdit, onStart, onDuplicate, onCorrect, editable = false, locked = false, move, deletion, hints }) {
  const agent = entry?.actor_type === 'agent';
  const needsReview = !!entry?.needs_review;
  const inline = !agent && editable && !!onCorrect;
  const moveOn = inline && !!move;
  // A description with a line break cannot round-trip through a single-line input, so it
  // stays read-only text and the dialog edits it.
  const multiline = /[\r\n]/.test(entry?.what ?? '');

  // One lock for the whole row: while a duplicate or a save is in flight every input and
  // the menu is disabled. The ref makes a second trigger a no-op even before React has
  // rendered the disabled state (Enter then the blur it causes).
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const [error, setError] = useState(null);
  const [errorField, setErrorField] = useState(null);
  const errorId = `row-error-${entry?.id ?? 'entry'}`;

  // What this row last saved, until the entry prop catches up. It is the baseline the
  // "unchanged" check compares against, so a blur after a save does not save again.
  const [saved, setSaved] = useState(null);
  const baseWhat = saved ? saved.description : entry?.what;
  const baseMinutes = saved ? saved.minutes : entry?.minutes;
  const baseAt = saved?.at ?? entry?.at;
  // The project the row shows: the entry's, or what a move saved until the refetch lands.
  const project = saved?.project ?? {
    client: entry?.client, client_id: entry?.client_id, task: entry?.task, task_id: entry?.task_id,
    service: entry?.service,
  };

  const committed = {
    description: capitalize(baseWhat?.trim() || ''),
    duration: fmtClock((Number(baseMinutes) || 0) * 60),
    end: toTimeInputValue(endOf({ at: baseAt, minutes: baseMinutes })),
    start: toTimeInputValue(baseAt),
    date: toDateInputValue(baseAt),
  };
  const [drafts, setDrafts] = useState(committed);

  // The committed values are the source of truth: when they change (a save landed, or
  // the entry prop was refetched) the drafts follow.
  useEffect(() => {
    setDrafts({ ...committed });
    clearDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committed.description, committed.duration, committed.end, committed.start, committed.date]);

  // The entry prop moved on (a refetch): drop the local baseline.
  useEffect(() => {
    setSaved(null);
  }, [entry?.what, entry?.minutes, entry?.at, entry?.client_id, entry?.task_id]);

  // Which fields the user has typed in since they were last committed or reverted. A blur
  // on a field that is not dirty does nothing.
  const dirty = useRef({ description: false, duration: false, end: false, start: false, date: false });
  const clearDirty = () => { dirty.current = { description: false, duration: false, end: false, start: false, date: false }; };

  // The date control is a calendar button that reveals a date input; the move dialog is
  // opened from the menu below `lg`, where the row hides those controls.
  const [dateOpen, setDateOpen] = useState(false);
  // Delete: the in-row confirmation is open. Nothing is sent until its Delete is pressed.
  const [confirming, setConfirming] = useState(false);
  const cancelRef = useRef(null);
  const rowRef = useRef(null);
  const [dialog, setDialog] = useState(null);
  const dateInputRef = useRef(null);

  // Set by Esc so the blur it triggers reverts instead of committing.
  const escaped = useRef(false);

  // The row's own save or duplicate, or another write on the page not yet refetched.
  const busy = pending || locked;

  /**
   * Runs `work` under the row's lock. Returns its result; the lock is released even if
   * it throws.
   */
  const underLock = async (work) => {
    inFlight.current = true;
    setPending(true);
    try {
      return await work();
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  /**
   * Logs a copy of the entry and surfaces a failure under the row.
   */
  const duplicate = async () => {
    if (inFlight.current || locked) return;
    setError(null);
    setErrorField(null);
    const result = await underLock(async () => {
      try {
        return await onDuplicate(entry);
      } catch (e) {
        return { status: 'failed', message: e?.message };
      }
    });
    if (result?.status === 'failed') setError(result.message || 'Could not duplicate this entry.');
  };

  const resetDrafts = () => {
    setDrafts({ ...committed });
    clearDirty();
  };

  /**
   * Closes the error box. The message and the `aria-invalid` / `aria-describedby` wiring
   * of the field that carried it go with it, and focus returns to that field if it is still
   * on screen, else to the row's menu. Never automatic: the user has to read an error.
   */
  const dismissError = () => {
    const field = errorField;
    setError(null);
    setErrorField(null);
    const byField = {
      description: 'row-description',
      duration: 'row-duration',
      end: 'row-end',
      start: 'row-start',
      date: 'row-date',
      project: 'row-project',
    };
    const root = rowRef.current;
    const candidates = [field && byField[field], 'row-menu'].filter(Boolean);
    for (const testId of candidates) {
      const target = root?.querySelector(`[data-testid="${testId}"]`);
      if (target && !target.disabled && isRendered(target, root)) {
        target.focus();
        return;
      }
    }
    // Nothing focusable is on screen at this width (a field hidden below a breakpoint, a
    // row locked): focus the row itself rather than let it fall to the page.
    root?.focus();
  };

  /** Shows `message` under the row, marks `field`, and puts the committed values back. */
  const fail = (message, field) => {
    setError(message);
    setErrorField(field);
    resetDrafts();
  };

  /**
   * Sends one correction under the row's lock and, when it lands, makes the row's baseline
   * what was sent, until the entry prop catches up with the refetch.
   *
   * `change.names` (the project's display names) stays on the row; only the fields the
   * handler needs are passed to `onCorrect`.
   *
   * @param {{minutes:number, description:string, when?:string, clientId?:string, taskId?:string, service?:string, names?:object}} change
   * @returns {Promise<{status:'saved'}|{status:'failed', message?:string}>}
   */
  const send = async (change) => {
    const { names, ...payload } = change;
    const result = await underLock(async () => {
      try {
        return await onCorrect(entry, payload);
      } catch (e) {
        return { status: 'failed', message: e?.message };
      }
    });
    if (result?.status === 'failed') return { status: 'failed', message: result.message };

    setSaved({
      description: change.description,
      minutes: change.minutes,
      at: change.when ?? baseAt,
      project: names
        ? {
          client: names.client,
          client_id: names.clientId,
          task: names.task,
          task_id: names.taskId,
          service: names.service,
        }
        : saved?.project,
    });
    return { status: 'saved' };
  };

  /**
   * Commits one field the user has edited. Returns `'saved'`, `'failed'` or `'skipped'`
   * (no call was made). The blur handler ignores the result.
   *
   * Skipped: the value is what the field already showed, or it is invalid (the field
   * reverts and the row says why), or a save is already in flight.
   *
   * @param {'description'|'duration'|'end'|'start'|'date'} field
   * @param {string} raw The field's value at blur.
   */
  const commit = async (field, raw) => {
    if (!inline || inFlight.current || locked) return 'skipped';

    const reject = (message, failedField) => {
      fail(message, failedField);
      return 'skipped';
    };
    const unchanged = () => {
      setError(null);
      setErrorField(null);
      resetDrafts();
      return 'skipped';
    };

    // Typing back what the field already showed is not a change (the parse below is
    // lossy: an end time has no seconds, a duration rounds).
    const text = raw.trim();
    if (text === committed[field]) return unchanged();

    let minutes = Number(baseMinutes) || 0;
    let description = baseWhat ?? '';
    let when;

    if (field === 'description') {
      if (!text) return reject('Enter a description', 'description');
      if (text === (baseWhat ?? '').trim()) return unchanged();
      description = text;
    } else if (field === 'start') {
      // The end stays where it is; the start moves and the duration follows.
      const resolved = resolveStartEdit({ at: baseAt, minutes: baseMinutes }, raw);
      if (resolved.error) {
        return reject(resolved.error === 'nonexistent' ? NONEXISTENT_TIME_MESSAGE : 'Enter a valid start time', 'start');
      }
      const sameStart = resolved.start.getTime() === floorToMinute(new Date(baseAt).getTime());
      if (sameStart && resolved.minutes === Number(baseMinutes)) return unchanged();
      // An earlier start can reach back across midnight onto a day the list does not show,
      // and the entry would vanish from it after the refetch.
      if (toDateInputValue(resolved.start) < move.minDate) {
        return reject('Pick a time within the days shown on this timesheet', 'start');
      }
      minutes = resolved.minutes;
      when = resolved.start.toISOString();
      if (!description.trim()) return reject('Enter a description', 'description');
    } else if (field === 'date') {
      if (!text) return reject('Pick a date', 'date');
      if (text < move.minDate || text > move.maxDate) {
        return reject('Pick a date within the days shown on this timesheet', 'date');
      }
      const moved = moveToDate(baseAt, text);
      if (moved.error) {
        return reject(moved.error === 'nonexistent' ? NONEXISTENT_TIME_MESSAGE : 'Pick a valid date', 'date');
      }
      when = moved.start.toISOString();
      if (!description.trim()) return reject('Enter a description', 'description');
    } else {
      const parsed = field === 'duration' ? parseDuration(raw) : minutesFromEndTime(baseAt, raw);
      if (parsed == null) return reject('Enter a valid duration', field);
      if (parsed === Number(baseMinutes)) return unchanged();
      minutes = parsed;
      // fn_effort_correct rejects an empty description, so say so before sending.
      if (!description.trim()) return reject('Enter a description', 'description');
    }

    setError(null);
    setErrorField(null);
    const result = await send(when === undefined ? { minutes, description } : { minutes, description, when });

    if (result.status === 'failed') {
      fail(result.message || 'Could not save this change.', field);
      return 'failed';
    }
    return 'saved';
  };

  /**
   * Commits a project chosen in the picker. Only what differs from the entry is sent: the
   * client when it changed, the task when one was chosen and it differs. Returns
   * `'saved'`, `'failed'` or `'skipped'`.
   *
   * @param {import('./ProjectPicker').ProjectChoice} choice
   */
  const commitProject = async (choice) => {
    if (!moveOn || inFlight.current || locked) return 'skipped';
    if (choice.label !== undefined) return commitLabel(choice.label);
    const clientChanged = choice.clientId !== project.client_id;
    const taskChanged = choice.taskId !== undefined && choice.taskId !== project.task_id;
    if (!clientChanged && !taskChanged) return 'skipped';
    const description = baseWhat ?? '';
    if (!description.trim()) {
      fail('Enter a description', 'description');
      return 'skipped';
    }

    setError(null);
    setErrorField(null);
    const change = {
      minutes: Number(baseMinutes) || 0,
      description,
      ...(clientChanged ? { clientId: choice.clientId } : {}),
      ...(taskChanged ? { taskId: choice.taskId } : {}),
      names: {
        clientId: choice.clientId,
        client: choice.clientName,
        taskId: choice.taskId ?? null,
        task: choice.taskName ?? null,
        service: project.service,
      },
    };
    const result = await send(change);
    if (result.status === 'failed') {
      fail(result.message || 'Could not save this change.', 'project');
      return 'failed';
    }
    return 'saved';
  };

  /**
   * Commits a free-text label as the entry's `service`. The text is normalised again here
   * (trimmed, whitespace collapsed, 1 to 60 characters) whatever the picker did. Nothing is
   * sent when it equals the current label. Only the label travels: the client and task are
   * left alone, and no time changed, so no overlap check runs.
   *
   * There is no way to clear a label: the backend reads a null as "keep", so editing
   * replaces the text until it names an explicit clear (§5f.1).
   *
   * @param {string} text
   * @returns {Promise<'saved'|'failed'|'skipped'>}
   */
  const commitLabel = async (text) => {
    if (!moveOn || inFlight.current || locked) return 'skipped';
    const label = normalizeLabel(text);
    if (label.empty || label.tooLong) {
      fail(label.empty ? 'Enter a label' : `Use ${MAX_LABEL_LENGTH} characters or fewer`, 'project');
      return 'skipped';
    }
    if (label.value === (project.service ?? '').trim()) return 'skipped';
    const description = baseWhat ?? '';
    if (!description.trim()) {
      fail('Enter a description', 'description');
      return 'skipped';
    }

    setError(null);
    setErrorField(null);
    const result = await send({
      minutes: Number(baseMinutes) || 0,
      description,
      service: label.value,
      names: {
        clientId: project.client_id,
        client: project.client,
        taskId: project.task_id,
        task: project.task,
        service: label.value,
      },
    });
    if (result.status === 'failed') {
      fail(result.message || 'Could not save this change.', 'project');
      return 'failed';
    }
    return 'saved';
  };

  const fieldHandlers = (field) => ({
    onChange: (e) => {
      dirty.current[field] = true;
      setDrafts((d) => ({ ...d, [field]: e.target.value }));
    },
    onBlur: (e) => {
      if (escaped.current) {
        escaped.current = false;
        return;
      }
      // Defence in depth: the displayed-value comparison in `commit` already skips an
      // untouched field, so removing this alone changes no outcome in practice. It is
      // kept because "did the user type here" must not depend on a lossy re-parse, and
      // because it stops an untouched blur from clearing another field's error message.
      if (!dirty.current[field]) return;
      commit(field, e.target.value);
    },
    onKeyDown: (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.currentTarget.blur();
      } else if (e.key === 'Escape') {
        // Only a focused input blurs, and only a blur consumes the flag.
        escaped.current = document.activeElement === e.currentTarget;
        resetDrafts();
        setError(null);
        setErrorField(null);
        e.currentTarget.blur();
      }
    },
    disabled: busy,
    ...invalidProps(errorField === field, errorId),
  });

  // The date control commits on blur or Enter, never on change: a browser fires change for
  // every valid date while a year is typed (0002, 0020, 0202, 2026), and the first would be
  // saved. Whatever the outcome, the input closes with the blur.
  const dateHandlers = () => {
    const base = fieldHandlers('date');
    return {
      ...base,
      onBlur: (e) => {
        base.onBlur(e);
        setDateOpen(false);
      },
    };
  };

  // Reveal the date input and open the native picker where the browser supports it. The
  // click that opened it is the user gesture `showPicker` requires.
  useEffect(() => {
    if (!dateOpen) return;
    const input = dateInputRef.current;
    if (!input) return;
    input.focus();
    try {
      input.showPicker?.();
    } catch {
      // not allowed without a gesture, or unsupported: the focused input still types
    }
  }, [dateOpen]);

  /**
   * The calendar button: opens the date input, or, when it is open, leaves it, which saves
   * a changed value (blur) and closes it.
   */
  const toggleDate = () => {
    if (!dateOpen) {
      setDateOpen(true);
      return;
    }
    const input = dateInputRef.current;
    if (input && document.activeElement === input) input.blur();
    else setDateOpen(false);
  };

  // A lock that lands while the date input is open closes it.
  useEffect(() => {
    if (busy) setDateOpen(false);
  }, [busy]);

  // Focus lands on Cancel when the confirmation opens, so Enter cannot delete by accident.
  useEffect(() => {
    if (confirming) cancelRef.current?.focus();
  }, [confirming]);

  /** Closes the confirmation without sending anything, and returns focus to the menu. */
  const cancelDelete = () => {
    if (inFlight.current) return;
    setConfirming(false);
    rowRef.current?.querySelector('[data-testid="row-menu"]')?.focus();
  };

  /**
   * Deletes the entry under the row's lock. A second press while it is in flight, or while
   * another write on the page is, sends nothing. On a refusal the confirmation closes and
   * the message shows under the row; on success the row is normally gone with the refetch.
   */
  const confirmDelete = async () => {
    if (!deletion?.onDelete || inFlight.current || locked) return;
    setError(null);
    setErrorField(null);
    // This row is about to unmount, taking focus with it. With no Undo toast to receive it,
    // pick where it goes now, while the row's neighbours can still be read.
    const heir = deletion.undoable ? null : focusHeir(rowRef.current);
    const result = await underLock(async () => {
      try {
        return await deletion.onDelete(entry);
      } catch (e) {
        return { status: 'failed', message: e?.message };
      }
    });
    setConfirming(false);
    if (result?.status === 'failed') setError(result.message || 'Could not delete this entry.');
    else if (heir) setTimeout(() => focusElement(heir), 0);
  };

  /** Sends a change made in the move dialog; the dialog shows a failure itself. */
  const submitDialog = async (change) => {
    if (inFlight.current || locked) return { status: 'failed', message: 'Another change is still saving. Try again.' };
    return send(change);
  };

  const hasDescription = !!entry?.what?.trim();
  const showMenu = !agent && editable && !!onDuplicate;
  // Delete lives in the menu, so it needs the menu: an editable human row on the caller's
  // own timesheet, with the capability on.
  const showDelete = showMenu && !!deletion?.onDelete;
  const showProject = !agent && (project.client || project.task);
  // An end time cannot express a day or more, so such an entry shows it as text.
  const endEditable = (Number(baseMinutes) || 0) < MAX_DURATION_MINUTES;
  // Inline editing replaces Edit on an editable human row; Verify (needs review) stays,
  // and so does Edit on a row a field cannot fully hold: a description with a line break,
  // or an entry of a day or more (the dialog can adjust it).
  const showEditButton = !inline || needsReview || multiline || !endEditable;

  const start = baseAt ? new Date(baseAt) : null;
  const durationSec = (Number(baseMinutes) || 0) * 60;
  const end = start ? new Date(start.getTime() + durationSec * 1000) : null;
  // A range that runs past midnight gets a "+1" like Toggl, so 11:58PM–4:28AM reads right.
  const crossesDay = start && end && fmtDate(start) !== fmtDate(end);

  const rawDescription = entry?.what?.trim() || 'No description';
  const description = capitalize(rawDescription);

  return (
    <div
      className="group relative border-b border-gray-100 dark:border-gray-800 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/40"
      ref={rowRef}
      tabIndex={-1}
      data-testid="effort-row"
      data-kind={agent ? 'agent' : 'human'}
      data-pending={pending ? 'true' : undefined}
    >
      <div className={`${ROW_WRAP.row} px-3 py-2.5`}>
        {/* Description leads — the person's own words. */}
        <div className={`min-w-[5rem] flex items-center gap-3 ${ROW_WRAP.description}`}>
          {agent && (
            <span data-testid="agent-badge" className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">
              Agent
            </span>
          )}
          {inline && !multiline ? (
            <input
              type="text"
              data-testid="row-description"
              aria-label="Description"
              placeholder="No description"
              autoComplete="off"
              value={drafts.description}
              title={drafts.description || undefined}
              className={`min-w-0 w-full truncate font-medium text-gray-900 dark:text-gray-100 ${INPUT_BASE}`}
              {...fieldHandlers('description')}
            />
          ) : (
            <span className={`truncate ${agent ? 'text-sm text-gray-500 dark:text-gray-400' : 'text-sm font-medium text-gray-900 dark:text-gray-100'}`} title={description}>
              {description}
            </span>
          )}
          {!agent && hints?.length > 0 && (
            <span className="flex shrink-0 items-center gap-1">
              {hints.map((hint) => (
                <span
                  key={hint.key}
                  data-testid="row-invalid"
                  data-reason={hint.key}
                  title={hint.text}
                  className="rounded bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 dark:text-amber-300 whitespace-nowrap"
                >
                  {hint.text}
                </span>
              ))}
            </span>
          )}
        </div>

        {/* Everything right of the description is one group: on the description's line from
            the wrap breakpoint up (its children then join the row's flex), on its own line
            below, left-aligned under the text. */}
        <div className={ROW_WRAP.group} data-testid="row-meta">
        {/* Project — "● Client · Task". The task is the JOIN between a human row and an
            agent row on the same work, so the substitution reads on one screen. An agent
            row shows its task alone (its cost, not a client, is its signal). Kept subtle
            and wide-screen-only; nothing renders when there is neither client nor task. */}
        {moveOn ? (
          <EntryProject
            entry={{ ...entry, ...project }}
            disabled={busy}
            loadClients={move.loadClients}
            loadTasks={move.loadTasks}
            onChoose={commitProject}
            idPrefix={`row-project-${entry?.id ?? 'entry'}`}
            buttonProps={invalidProps(errorField === 'project', errorId)}
          />
        ) : (showProject || (agent && entry?.task)) && (
          <div className="hidden lg:flex min-w-0 shrink items-center gap-1.5 max-w-[20rem] text-sm text-gray-500 dark:text-gray-400" data-testid="row-project">
            {showProject && (
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#32B1FF]" aria-hidden="true" data-testid="row-project-dot" />
            )}
            {!agent && project.client && (
              project.client_id ? (
                <ClientSlideOut clientId={project.client_id} clientName={project.client} className="min-w-0 truncate max-w-[10rem]" />
              ) : (
                <span className="min-w-0 truncate max-w-[10rem]" data-testid="row-client">{project.client}</span>
              )
            )}
            {!agent && project.client && project.task && <span aria-hidden="true">·</span>}
            {project.task && (
              <span className="min-w-0 truncate max-w-[12rem]" data-testid="row-task" title={project.task}>
                {project.task}
              </span>
            )}
          </div>
        )}

        {/* Right rail — time range · duration · play · ⋯ menu · edit/verify. */}
        <div className="shrink-0 flex items-center gap-3">
          {/* Clock range (human only — an agent run has no wall-clock window), with a
              clock icon before the times. Editable rows show the start (read-only for
              now) and an end time that sets the duration. */}
          {!agent && start && (
            inline ? (
              <span className="hidden md:flex items-center gap-1.5 text-sm tabular-nums text-gray-500 dark:text-gray-400" data-testid="row-time-range" title="Logged time range (start – end)">
                <span className="text-gray-400 dark:text-gray-500" aria-hidden="true">
                  <Icon className="w-4 h-4" path={<><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></>} />
                </span>
                {moveOn && endEditable ? (
                  <input
                    type="time"
                    data-testid="row-start"
                    aria-label="Start time"
                    title="Start time. The end stays where it is; a time later than the end means the day before."
                    value={drafts.start}
                    className={`hidden lg:block w-[5.5rem] tabular-nums text-gray-700 dark:text-gray-200 ${INPUT_BASE}`}
                    {...fieldHandlers('start')}
                  />
                ) : (
                  <input
                    type="time"
                    data-testid="row-start"
                    aria-label="Start time"
                    title={moveOn ? "Start time can't be changed on an entry of a day or more" : "Start time can't be changed yet"}
                    value={toTimeInputValue(start)}
                    disabled
                    readOnly
                    className={`hidden lg:block w-[5.5rem] tabular-nums text-gray-500 dark:text-gray-400 ${INPUT_BASE}`}
                  />
                )}
                <span aria-hidden="true">–</span>
                <span className="relative">
                  {endEditable ? (
                    <input
                      type="time"
                      data-testid="row-end"
                      aria-label="End time"
                      title="End time"
                      value={drafts.end}
                      className={`w-[5.5rem] tabular-nums text-gray-700 dark:text-gray-200 ${INPUT_BASE}`}
                      {...fieldHandlers('end')}
                    />
                  ) : (
                    <span className="px-1.5" title="End time">{fmtTimeOfDay(end)}</span>
                  )}
                  {crossesDay && <sup className="absolute -right-3 top-0 text-[10px] text-gray-400">+1</sup>}
                </span>
                {moveOn && (
                  <span className="hidden lg:flex items-center gap-1">
                    <button
                      type="button"
                      data-testid="row-date"
                      aria-label="Change date"
                      title="Change date"
                      disabled={busy}
                      // Keep focus in the date input, so the click below is not preceded by a
                      // blur that commits and closes it (which the click would then reopen).
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={toggleDate}
                      className="rounded-md p-1 text-gray-400 hover:text-[#32B1FF] hover:bg-[#32B1FF]/10 focus:outline-none focus:ring-1 focus:ring-[#32B1FF] disabled:opacity-60 aria-[invalid=true]:text-red-500"
                      {...invalidProps(errorField === 'date', errorId)}
                    >
                      <Icon path={<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>} />
                    </button>
                    {dateOpen && (
                      <input
                        ref={dateInputRef}
                        type="date"
                        data-testid="row-date-input"
                        aria-label="Date"
                        min={move.minDate}
                        max={move.maxDate}
                        value={drafts.date}
                        className={`w-[9.5rem] tabular-nums text-gray-700 dark:text-gray-200 ${INPUT_BASE}`}
                        {...dateHandlers()}
                      />
                    )}
                  </span>
                )}
              </span>
            ) : (
              <span className="hidden md:flex w-[168px] justify-start items-center gap-1.5 text-sm tabular-nums text-gray-500 dark:text-gray-400" data-testid="row-time-range" title="Logged time range (start – end)">
                <span className="text-gray-400 dark:text-gray-500" aria-hidden="true">
                  <Icon className="w-4 h-4" path={<><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></>} />
                </span>
                <span>{fmtTimeOfDay(start)}</span>
                <span aria-hidden="true">–</span>
                <span className="relative">
                  {fmtTimeOfDay(end)}
                  {crossesDay && <sup className="ml-0.5 text-[10px] text-gray-400">+1</sup>}
                </span>
              </span>
            )
          )}

          {/* Duration (human) or cost (agent) */}
          {agent ? (
            <span data-testid="row-cost" className="w-[86px] text-right text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
              {fmtCost(entry?.cost)}
            </span>
          ) : inline ? (
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              data-testid="row-duration"
              aria-label="Duration"
              title="Hours:minutes, hours:minutes:seconds, or a number of minutes"
              value={drafts.duration}
              className={`w-[5.5rem] text-right font-semibold tabular-nums ${needsReview ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'} ${INPUT_BASE}`}
              {...fieldHandlers('duration')}
            />
          ) : (
            <span data-testid="row-duration" className={`w-[86px] text-right text-sm font-semibold tabular-nums ${needsReview ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'}`}>
              {fmtClock(durationSec)}
            </span>
          )}

          {/* Start a fresh timer from this entry (human only) */}
          {!agent && onStart && (
            <button
              type="button"
              onClick={() => onStart(entry)}
              className="p-1 rounded-full text-gray-400 hover:text-[#32B1FF] hover:bg-[#32B1FF]/10"
              aria-label={`Start a timer for ${description}`}
              title={`Start a new timer for "${description}"`}
              data-testid="row-play"
            >
              <Icon path={<polygon points="6 3 20 12 6 21 6 3" />} />
            </button>
          )}

          {/* ⋯ — the row's secondary actions (human, own timesheet only). */}
          {showMenu && (
            <EntryMenu
              onDuplicate={duplicate}
              duplicateDisabled={!hasDescription}
              duplicateTitle={!hasDescription ? 'Add a description before duplicating' : undefined}
              disabled={busy}
              onDelete={showDelete ? () => setConfirming(true) : undefined}
              {...(moveOn ? {
                onChangeProject: () => setDialog('project'),
                onChangeDate: () => setDialog('date'),
                onChangeTime: endEditable ? () => setDialog('time') : undefined,
              } : {})}
            />
          )}

          {/* Edit / verify — a direct pencil. For an entry that needs review it reads as
              "Verify", opening the correction dialog. Absent on an inline-editable row,
              where the fields themselves are the edit. */}
          {showEditButton && (
            <button
              type="button"
              onClick={() => onEdit?.(entry)}
              disabled={busy}
              className={`inline-flex items-center justify-center gap-1.5 w-[88px] px-2 py-1 rounded-md border text-sm font-medium disabled:opacity-50 disabled:pointer-events-none ${
                needsReview
                  ? 'bg-orange-50 dark:bg-orange-900/25 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/40'
                  : 'bg-sky-50 dark:bg-sky-900/25 border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/40'
              }`}
              aria-label={needsReview ? 'Verify this entry' : 'Edit this entry'}
              title={needsReview ? 'Verify this entry' : 'Edit this entry'}
              data-testid={needsReview ? 'verify-correct' : 'edit-entry'}
            >
              <Icon path={<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" /></>} />
              <span>{needsReview ? 'Verify' : 'Edit'}</span>
            </button>
          )}
        </div>
        </div>
      </div>

      {showDelete && confirming && (
        <div
          role="alertdialog"
          aria-labelledby={`${errorId}-confirm`}
          data-testid="row-delete-confirm"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              cancelDelete();
            }
          }}
          className="mx-3 mb-2 flex flex-wrap items-center gap-3 rounded-md border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 px-3 py-2"
        >
          <p id={`${errorId}-confirm`} className="text-sm text-red-800 dark:text-red-200">
            Delete this entry?{deletion.undoable ? '' : " This can't be undone."}
          </p>
          <div className="ml-auto flex gap-2">
            <button
              ref={cancelRef}
              type="button"
              onClick={cancelDelete}
              disabled={pending}
              data-testid="row-delete-cancel"
              className="rounded-md px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-white/60 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={busy}
              data-testid="row-delete-yes"
              className="rounded-md bg-red-600 px-3 py-1 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {moveOn && dateOpen && drafts.date !== committed.date && (
        <p role="status" className="px-3 pb-2 text-xs text-gray-500 dark:text-gray-400" data-testid="row-date-hint">
          Press Enter or click away to save the new date.
        </p>
      )}

      {moveOn && pending && move.checking && (
        <p role="status" className="px-3 pb-2 text-sm text-gray-500 dark:text-gray-400" data-testid="row-checking">
          Checking…
        </p>
      )}

      {error && (
        <div
          role="alert"
          data-testid="row-error"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              dismissError();
            }
          }}
          className="mx-3 mb-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-200"
        >
          <span id={errorId} className="min-w-0 flex-1 break-words">{error}</span>
          <button
            type="button"
            onClick={dismissError}
            aria-label="Dismiss"
            title="Dismiss"
            data-testid="row-error-dismiss"
            className="-mr-1 -mt-0.5 shrink-0 rounded p-1 text-red-500 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-900/40 focus:outline-none focus:ring-1 focus:ring-red-400"
          >
            <Icon className="h-3.5 w-3.5" path={<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>} />
          </button>
        </div>
      )}

      {moveOn && dialog && (
        <EntryMoveDialog
          entry={{ ...entry, ...project, at: baseAt, minutes: baseMinutes, what: baseWhat }}
          focus={dialog}
          bounds={{ minDate: move.minDate, maxDate: move.maxDate }}
          loadClients={move.loadClients}
          loadTasks={move.loadTasks}
          onSubmit={submitDialog}
          onClose={() => setDialog(null)}
          idPrefix={`move-${entry?.id ?? 'entry'}`}
        />
      )}
    </div>
  );
}
